import { z } from "zod";

const equipmentDegressivePriceSchema = z.object({
  id: z.string().optional(),
  discountId: z.string().optional(),
  minDays: z.coerce.number().int().min(2, "Le nombre de jours doit être au moins 2"),
  cumulativePriceHT: z.coerce.number().nullable(),
  onQuote: z.boolean().default(false),
});

export type EquipmentDegressivePriceTier = z.infer<typeof equipmentDegressivePriceSchema>;

export const updateEquipmentPricingSchema = z
  .object({
    basePriceHT: z.coerce.number().min(0, "Le prix de base doit être positif"),
    VAT: z.coerce.number(),
    degressivePriceTiers: z.array(equipmentDegressivePriceSchema),
  })
  .refine(
    (data) => {
      const onQuoteTiers = data.degressivePriceTiers.filter((t) => t.onQuote);
      if (onQuoteTiers.length === 0) return true;
      if (onQuoteTiers.length > 1) return false;

      const sorted = [...data.degressivePriceTiers].sort((a, b) => a.minDays - b.minDays);
      const lastTier = sorted[sorted.length - 1];
      return lastTier?.onQuote === true;
    },
    {
      message: '"Sur devis" ne peut être appliqué qu\'au dernier palier',
      path: ["degressivePriceTiers"],
    },
  )
  .refine(
    (data) =>
      data.degressivePriceTiers.every(
        (t) => t.onQuote || (t.cumulativePriceHT !== null && t.cumulativePriceHT >= 0),
      ),
    {
      message: "Chaque palier doit avoir un prix ou être sur devis",
      path: ["degressivePriceTiers"],
    },
  )
  .refine(
    (data) => {
      const days = data.degressivePriceTiers.map((t) => t.minDays);
      const unique = new Set(days);
      return unique.size === days.length;
    },
    {
      message: "Les paliers de jours doivent être uniques",
      path: ["degressivePriceTiers"],
    },
  )
  .refine(
    (data) => {
      const sorted = [...data.degressivePriceTiers]
        .filter((t) => !t.onQuote && t.cumulativePriceHT !== null)
        .sort((a, b) => a.minDays - b.minDays);

      let previousPrice = data.basePriceHT;
      for (const tier of sorted) {
        if (tier.cumulativePriceHT! <= previousPrice) {
          return false;
        }
        previousPrice = tier.cumulativePriceHT!;
      }
      return true;
    },
    {
      message: "Les prix cumulatifs doivent être croissants",
      path: ["degressivePriceTiers"],
    },
  );

export type UpdateEquipmentPricingForm = z.infer<typeof updateEquipmentPricingSchema>;

export const defaultEquipmentDegressivePriceTier: EquipmentDegressivePriceTier = {
  minDays: 2,
  cumulativePriceHT: null,
  onQuote: false,
};

export const defaultUpdateEquipmentPricingValues: UpdateEquipmentPricingForm = {
  basePriceHT: 0,
  VAT: 20,
  degressivePriceTiers: [],
};
